import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NotificationChannelModel } from "app/models/notificationChannel.model";
import { ProjectModel } from "app/models/project.model";
import { Observable } from "rxjs";
import { share } from "rxjs/operators";
import { UserModel } from "../../models/user.model";
import { AuthenticationService } from "../authentication.service";
import { ApiService } from "./api.service";

@Injectable()
export class ProjectService extends ApiService {
    constructor(
        protected http: HttpClient,
        private authService: AuthenticationService,
    ) {
        super(http);
        this.apiEndpoint = "project";
    }

    get(id: string): Observable<ProjectModel> {
        return this.http.get<ProjectModel>(this.getUrl(id));
    }

    getAll(project?: object): Observable<ProjectModel[]> {
        return this.http.get<ProjectModel[]>(this.getUrl(""), project);
    }

    create(project: ProjectModel): Observable<ProjectModel> {
        const obs = this.http.post<ProjectModel>(this.getUrl(""), project).pipe(share());
        obs.subscribe(() => {
            // Force reload of user in case of data edit
            this.authService.me().subscribe();
        });
        return obs;
    }

    edit(project: ProjectModel): Observable<ProjectModel> {
        const obs = this.http.put<ProjectModel>(this.getUrl(""), project).pipe(share());
        obs.subscribe(() => {
            // Force reload of user in case of data edit
            this.authService.me().subscribe();
        });
        return obs;
    }

    delete(id: string) {
        const obs = this.http.delete(this.getUrl(String(id)));
        obs.subscribe(() => {
            // Force reload of user in case of data edit
            this.authService.me().subscribe();
        });
        return obs.pipe(share());
    }

    getNotificationChannels(projectId: string): Observable<NotificationChannelModel[]> {
        return this.http.get<NotificationChannelModel[]>(this.getUrl(`${projectId}/notification-channel`));
    }

    addNotificationChannel(projectId: string, channel: NotificationChannelModel): Observable<NotificationChannelModel> {
        const obs = this.http.post<NotificationChannelModel>(this.getUrl(`${projectId}/notification-channel`), channel).pipe(share());
        obs.subscribe(() => this.authService.me().subscribe());
        return obs;
    }

    editNotificationChannel(projectId: string, channelId: string, channel: NotificationChannelModel): Observable<NotificationChannelModel> {
        const obs = this.http
            .put<NotificationChannelModel>(this.getUrl(`${projectId}/notification-channel/${channelId}`), channel)
            .pipe(share());
        obs.subscribe(() => this.authService.me().subscribe());
        return obs;
    }

    deleteNotificationChannel(projectId: string, channelId: string): Observable<NotificationChannelModel> {
        const obs = this.http.delete<NotificationChannelModel>(this.getUrl(`${projectId}/notification-channel/${channelId}`)).pipe(share());
        obs.subscribe(() => this.authService.me().subscribe());
        return obs;
    }

    testNotificationChannel(projectId: string, channelId: string): Observable<void> {
        return this.http.post<void>(this.getUrl(`${projectId}/notification-channel/${channelId}/test`), {});
    }

    disableBrowserNotifications(projectId: string): Observable<UserModel> {
        return this.http.post<UserModel>(
            this.getUrl(`${projectId}/browser-notification-disable`),
            {},
        );
    }

    enableBrowserNotifications(projectId: string): Observable<UserModel> {
        return this.http.request<UserModel>(
            "delete",
            this.getUrl(`${projectId}/browser-notification-disable`),
        );
    }
}
